import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	FormEvent,
	KeyboardEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { MarkdownHooks } from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { getUniqueIdMap } from "../../../../../common/generateUniqueId";
import SaveFormat from "../../../../../common/save/SaveFormat";
import { env } from "../../../../../env/env";
import { selectEditor } from "../../../../editor/editorSlice";
import { selectFetchLogicsStatus } from "../../../../logics/logicsSlice";
import { saveVersion } from "../../../../menuManager/menu/menuImpl/ProjectMenu";
import { WindowTypeAndPropsDefinition } from "../../../windowManagerSlice";
import {
	applyLoadedProject,
	loadProject,
} from "../LoadProjectWindow/LoadProjectWindow";
import css from "./ChatWindow.module.css";
import {
	extractSaveFormatFromText,
	stripSaveFormatJson,
} from "./extractSaveFormatFromText";

export interface ChatWindowProps {}

export type ChatWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"chat",
	ChatWindowProps
>;

interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	content: string;
}

interface TalkWithAiResponse {
	agentId: string;
	runId: string;
	status: string;
	result: string | null;
	ppf?: string | null;
}

function decodeBase64(base64: string): Uint8Array {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);

	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}

	return bytes;
}

interface TalkWithAiError {
	error?: string;
}

export function ChatWindow(props: ChatWindowProps & { id: string }) {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const editor = useAppSelector(selectEditor);
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [input, setInput] = useState("");
	const [agentId, setAgentId] = useState<string | undefined>();
	const [isSending, setIsSending] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const getProjectSaveFormat = ():
		| SaveFormat
		| Omit<SaveFormat, "logicsCommitRef"> => {
		const project = {
			editor,
			saveVersion,
			idMap: getUniqueIdMap(),
		};

		if (logicsStatus.code === "succeeded") {
			return {
				...project,
				logicsCommitRef: logicsStatus.logics.commit,
			};
		}

		return project;
	};

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, isSending]);

	const sendMessage = async () => {
		const message = input.trim();
		if (!message || isSending) {
			return;
		}

		setMessages((prev) => [
			...prev,
			{
				id: `user-${Date.now()}`,
				role: "user",
				content: message,
			},
		]);
		setInput("");
		setIsSending(true);

		try {
			const response = await fetch(`${env.apiUrl}/talkwithai`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					message,
					saveFormat: getProjectSaveFormat(),
					...(agentId ? { agentId } : {}),
				}),
			});

			const payload = (await response.json()) as
				| TalkWithAiResponse
				| TalkWithAiError;

			if (!response.ok) {
				throw new Error(
					("error" in payload && payload.error) || t("chatError"),
				);
			}

			const data = payload as TalkWithAiResponse;
			setAgentId(data.agentId);

			let assistantText =
				data.result?.trim() ||
				(data.status === "error" ? t("chatError") : t("chatEmptyReply"));
			const saveFormatFromText = extractSaveFormatFromText(
				data.result ?? "",
			);

			try {
				if (data.ppf) {
					loadProject((saveFormat) => {
						applyLoadedProject(dispatch, saveFormat);
					}, decodeBase64(data.ppf));
				} else if (saveFormatFromText) {
					applyLoadedProject(dispatch, saveFormatFromText);
				}

				if (saveFormatFromText) {
					const withoutJson = stripSaveFormatJson(assistantText);
					if (withoutJson && withoutJson !== assistantText) {
						assistantText = withoutJson;
					}
				}
			} catch (loadError) {
				toast.error(
					loadError instanceof Error ? loadError.message : t("chatError"),
				);
			}

			setMessages((prev) => [
				...prev,
				{
					id: data.runId || `assistant-${Date.now()}`,
					role: "assistant",
					content: assistantText,
				},
			]);
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : t("chatError");
			toast.error(errorMessage);
			setMessages((prev) => [
				...prev,
				{
					id: `error-${Date.now()}`,
					role: "assistant",
					content: errorMessage,
				},
			]);
		} finally {
			setIsSending(false);
			textareaRef.current?.focus();
		}
	};

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		void sendMessage();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			void sendMessage();
		}
	};

	return (
		<div className={css.content}>
			<div className={css.messages}>
				{messages.length === 0 && !isSending && (
					<div className={css.emptyState}>{t("chatPlaceholder")}</div>
				)}
				{messages.map((message) => (
					<div
						key={message.id}
						className={
							message.role === "user" ? css.userBubble : css.assistantBubble
						}
					>
						{message.role === "assistant" ? (
							<MarkdownHooks remarkPlugins={[remarkGfm]}>
								{message.content}
							</MarkdownHooks>
						) : (
							message.content
						)}
					</div>
				))}
				{isSending && (
					<div className={css.assistantBubble}>
						<span className={css.typing}>{t("chatThinking")}</span>
					</div>
				)}
				<div ref={messagesEndRef} />
			</div>
			<form className={css.composer} onSubmit={handleSubmit}>
				<textarea
					ref={textareaRef}
					className={css.input}
					value={input}
					onChange={(event) => setInput(event.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={t("chatInputPlaceholder")}
					disabled={isSending}
					rows={2}
				/>
				<button
					className={css.sendButton}
					type="submit"
					disabled={isSending || !input.trim()}
					aria-label={t("Chat")}
				>
					<FontAwesomeIcon icon={faPaperPlane} />
				</button>
			</form>
		</div>
	);
}
